"use client";

import React, { useEffect, useState } from "react";
import { Modal, ModalBody, ModalContent, ModalHeader, Spinner } from "@nextui-org/react";
import { Icon } from "@iconify/react";

export type AttachmentMediaOverlayState = { kind: "video" | "audio"; url: string; name: string } | null;

/** Create/revoke object URL for a File (shared by prompt input and chat message previews). */
export function useObjectUrl(file: File | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url;
}

export function openLocalFileInNewTab(file: File, revokeAfterMs = 120_000): void {
  const u = URL.createObjectURL(file);
  globalThis.open(u, "_blank", "noopener,noreferrer");
  globalThis.setTimeout(() => URL.revokeObjectURL(u), revokeAfterMs);
}

const CARD_ROW =
  "flex h-12 min-w-[100px] max-w-[200px] rounded-md border border-default-200 bg-default-50 overflow-hidden flex-shrink-0 text-left transition-opacity disabled:opacity-60";
const CARD_LEFT_MEDIA =
  "w-12 h-12 flex-shrink-0 flex items-center justify-center bg-black border-r border-default-200 overflow-hidden";
const CARD_LEFT_NEUTRAL =
  "w-12 h-12 flex-shrink-0 flex items-center justify-center bg-default-100 border-r border-default-200 overflow-hidden";
const CARD_RIGHT = "flex flex-col justify-center gap-0.5 px-2 py-0.5 min-w-0 flex-1";
const LABEL_CLS = "text-[10px] font-semibold uppercase text-primary leading-tight";
const NAME_CLS = "text-xs text-default-600 truncate w-full leading-tight";

export function AttachmentCornerRemoveButton(props: Readonly<{ onClick: () => void; "aria-label"?: string }>) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        props.onClick();
      }}
      className="absolute -top-1 -right-1 z-10 w-5 h-5 rounded-full bg-default-200 hover:bg-default-300 flex items-center justify-center opacity-90 group-hover:opacity-100"
      aria-label={props["aria-label"] ?? "Remove file"}
    >
      <Icon icon="zondicons:close-solid" width="12" height="12" />
    </button>
  );
}

/** Horizontal file card: thumbnail/icon left, type label + file name right (PDF, Video, Audio, etc.). */
export function AttachmentFileTypeCard(props: Readonly<{
  label: string;
  fileName: string;
  loading?: boolean;
  onClick: () => void;
  leftContent: React.ReactNode;
  leftVariant?: "media" | "neutral";
  disabled?: boolean;
}>) {
  const { label, fileName, loading, onClick, leftContent, leftVariant = "neutral", disabled } = props;
  const leftClass = leftVariant === "media" ? CARD_LEFT_MEDIA : CARD_LEFT_NEUTRAL;
  return (
    <button type="button" onClick={onClick} disabled={disabled || loading} className={CARD_ROW}>
      <div className={leftClass}>{loading ? <Spinner size="sm" /> : leftContent}</div>
      <div className={CARD_RIGHT}>
        <span className={LABEL_CLS}>{label}</span>
        <span className={NAME_CLS} title={fileName}>
          {fileName}
        </span>
      </div>
    </button>
  );
}

export function AttachmentDocumentLeftIcon() {
  return <Icon icon="mdi:file-document-outline" width={22} height={22} className="text-primary" />;
}

export function AttachmentAudioLeftIcon() {
  return <Icon icon="mdi:music-note" width={22} height={22} className="text-primary" />;
}

/** Shared video/audio full-screen preview modal. */
export function AttachmentMediaModal(props: Readonly<{
  overlay: AttachmentMediaOverlayState;
  onClose: () => void;
  size?: "3xl" | "4xl";
}>) {
  const { overlay, onClose, size = "3xl" } = props;
  return (
    <Modal isOpen={overlay != null} onClose={onClose} size={size} isDismissable>
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">{overlay?.name}</ModalHeader>
        <ModalBody className="flex flex-col items-stretch gap-4 pb-6">
          {overlay?.kind === "video" && (
            <video src={overlay.url} controls className="w-full max-h-[70vh] rounded-lg bg-black" playsInline>
              <track kind="captions" />
            </video>
          )}
          {overlay?.kind === "audio" && (
            <div className="flex flex-col items-center gap-4 py-4">
              <Icon icon="mdi:music-circle" width={64} height={64} className="text-primary" />
              <audio src={overlay.url} controls className="w-full max-w-md">
                <track kind="captions" />
              </audio>
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
